import { TenantSwitcher } from "@/components/shared/TenantSwitcher";
import { Avatar } from "@/components/ui/Avatar";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
} from "@/components/ui/DropdownMenu";
import { cn } from "@/lib/utils";
import { buildBookingUrl } from "@/lib/bookingLinks";
import { clearAllQueries } from "@/lib/query-client";
import { useUIStore } from "@/stores/ui-store";
import { useAuth, useUser } from "@/lib/auth";
import { useBookingFlows } from "@/hooks/api/useBookingFlows";
import {
  BarChart3,
  Calendar,
  CalendarClock,
  ChevronDown,
  Clock,
  Menu,
  Copy,
  ExternalLink,
  HelpCircle,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Plus,
  Settings,
  Sliders,
  Users,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";

export function Sidebar() {
  const location = useLocation();
  const selectedBookingFlow = useUIStore((s) => s.selectedBookingFlow);
  const setSelectedBookingFlow = useUIStore((s) => s.setSelectedBookingFlow);
  const currentTenant = useUIStore((s) => s.currentTenant);
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const setSidebarCollapsed = useUIStore((s) => s.setSidebarCollapsed);
  const { signOut } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const { data: bookingFlows } = useBookingFlows();

  const [showFlowDropdown, setShowFlowDropdown] = useState(false);
  const flowDropdownRef = useRef<HTMLDivElement>(null);

  const isExpanded = !sidebarCollapsed;

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleSignOut = async () => {
    // Clear all TanStack Query cache
    clearAllQueries();

    // Clear all localStorage data
    localStorage.clear();

    // Sign out from Clerk and redirect to home
    await signOut({ redirectUrl: "/" });

    // Fallback navigation in case redirectUrl doesn't work
    navigate("/", { replace: true });
  };

  // Action handlers for Copy Link and Preview. Both go through buildBookingUrl
  // so this matches the Share Booking Link card exactly — they used to differ.
  const handleCopyPublicLink = () => {
    if (selectedBookingFlow && currentTenant) {
      navigator.clipboard.writeText(
        buildBookingUrl({
          tenantSlug: currentTenant,
          flowSlug: selectedBookingFlow,
        }),
      );
      // Could add a toast notification here
    }
  };

  const handlePreview = () => {
    if (selectedBookingFlow && currentTenant) {
      // preview: true only here — we are opening this ourselves, so it is
      // internal traffic. The copied link above must stay clean.
      window.open(
        buildBookingUrl({
          tenantSlug: currentTenant,
          flowSlug: selectedBookingFlow,
          preview: true,
        }),
        "_blank",
      );
    }
  };

  // Handle click outside for flow dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        flowDropdownRef.current &&
        !flowDropdownRef.current.contains(event.target as Node)
      ) {
        setShowFlowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 h-screen z-50",
        "bg-background-secondary border-r border-border-light",
        "transition-[width] duration-300 ease-in-out",
        isExpanded ? "w-64" : "w-16",
        "flex flex-col",
      )}
    >
      {/* Toggle Button */}
      <button
        onClick={handleToggleSidebar}
        className={cn(
          "absolute -right-3 top-6 z-10",
          "w-6 h-6 rounded-full",
          "bg-background-secondary border border-border-light",
          "flex items-center justify-center",
          "hover:bg-background-tertiary transition-colors",
          "shadow-sm",
        )}
        title={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
      >
        <Menu className="w-4 h-4 text-text-secondary" />
      </button>

      {/* Booking Flow Selector at Top */}
      <div
        className={cn(
          "border-b border-border-light/30 transition-all duration-300",
          isExpanded ? "p-4 pb-3" : "p-2 py-4",
        )}
      >
        {bookingFlows && bookingFlows.length > 0 ? (
          isExpanded ? (
            <div ref={flowDropdownRef}>
              <button
                onClick={() => setShowFlowDropdown(!showFlowDropdown)}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 transition-all w-full justify-start min-h-[40px]",
                  "hover:bg-background-tertiary text-text-secondary hover:text-text-primary border border-border-light",
                  showFlowDropdown
                    ? "bg-background-secondary rounded-t-lg border-b-0"
                    : "rounded-lg",
                )}
              >
                <Calendar className="w-5 h-5 flex-shrink-0" />
                <div className="flex-1 min-w-0 text-left overflow-hidden">
                  <div className="text-sm font-medium text-text-primary whitespace-nowrap text-ellipsis overflow-hidden">
                    {bookingFlows.find((f) => f.slug === selectedBookingFlow)
                      ?.name || "Select a flow..."}
                  </div>
                </div>
                <ChevronDown
                  className={cn(
                    "w-4 h-4 text-text-secondary transition-transform flex-shrink-0",
                    showFlowDropdown ? "rotate-180" : "",
                  )}
                />
              </button>

              <div
                className={cn(
                  "transition-all duration-200 overflow-hidden",
                  showFlowDropdown ? "max-h-64" : "max-h-0",
                )}
              >
                <div className="bg-background-secondary border border-border-light border-t-0 rounded-b-lg overflow-y-auto max-h-64">
                  {bookingFlows.map((flow) => (
                    <button
                      key={flow.slug}
                      onClick={() => {
                        setSelectedBookingFlow(flow.slug);
                        navigate(
                          `/booking-flows?flow=${flow.slug}&tab=overview`,
                        );
                        setShowFlowDropdown(false);
                      }}
                      className={cn(
                        "w-full px-4 py-2.5 text-left hover:bg-background-tertiary transition-colors",
                        flow.slug === selectedBookingFlow &&
                          "bg-background-tertiary",
                      )}
                    >
                      <div className="text-sm font-medium text-text-primary">
                        {flow.name}
                      </div>
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      navigate("/dashboard");
                      setShowFlowDropdown(false);
                    }}
                    className="w-full px-4 py-2.5 text-left hover:bg-background-tertiary transition-colors flex items-center gap-2 border-t border-border-light/30"
                  >
                    <Plus className="w-4 h-4 text-primary" />
                    <div className="text-sm font-medium text-text-primary">
                      Create Flow
                    </div>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <Calendar className="w-5 h-5 text-primary" />
            </div>
          )
        ) : (
          isExpanded && (
            <div className="text-xs text-text-secondary text-center py-2 animate-in fade-in duration-200">
              No flows yet. Create one from the dashboard.
            </div>
          )
        )}

        {/* Copy Link and Preview buttons - only show when a flow is selected */}
        {selectedBookingFlow && (
          <div
            className={cn(
              "flex gap-2 transition-all duration-300",
              isExpanded
                ? "mt-2 opacity-100"
                : "mt-0 opacity-0 h-0 overflow-hidden",
            )}
          >
            <button
              onClick={handleCopyPublicLink}
              className="flex-1 flex items-center justify-center px-2 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary bg-background-tertiary/50 hover:bg-background-tertiary rounded-lg transition-colors min-w-0"
              title="Copy public booking link"
            >
              <Copy className="w-3.5 h-3.5 flex-shrink-0" />
              <span
                className={cn(
                  "transition-all duration-300 whitespace-nowrap",
                  isExpanded
                    ? "opacity-100 ml-1.5"
                    : "opacity-0 ml-0 w-0 overflow-hidden",
                )}
              >
                Copy Link
              </span>
            </button>
            <button
              onClick={handlePreview}
              className="flex-1 flex items-center justify-center px-2 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary bg-background-tertiary/50 hover:bg-background-tertiary rounded-lg transition-colors min-w-0"
              title="Preview booking flow"
            >
              <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
              <span
                className={cn(
                  "transition-all duration-300 whitespace-nowrap",
                  isExpanded
                    ? "opacity-100 ml-1.5"
                    : "opacity-0 ml-0 w-0 overflow-hidden",
                )}
              >
                Preview
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Navigation - only show when a flow is selected */}
      {selectedBookingFlow ? (
        <nav
          className={cn(
            "flex-1 transition-all duration-300 overflow-y-auto scrollbar-thin",
            isExpanded ? "px-4 pt-3 pb-4 space-y-1" : "p-2 py-3 space-y-1",
          )}
        >
          <Link
            to={`/booking-flows?flow=${selectedBookingFlow}&tab=overview`}
            className={cn(
              "flex items-center rounded-xl text-sm font-medium transition-all duration-200 mt-1",
              "hover:bg-background-tertiary/50",
              location.pathname === "/booking-flows" &&
                (!location.search.includes("tab=") ||
                  location.search.includes("tab=overview"))
                ? "bg-primary-light text-primary"
                : "text-text-secondary hover:text-text-primary",
              isExpanded ? "px-3 py-2.5" : "justify-center p-2",
            )}
            title={!isExpanded ? "Overview" : undefined}
          >
            <LayoutGrid className={cn("w-5 h-5 flex-shrink-0")} />
            <span
              className={cn(
                "transition-all duration-300 whitespace-nowrap",
                isExpanded
                  ? "opacity-100 ml-3"
                  : "opacity-0 ml-0 w-0 overflow-hidden",
              )}
            >
              Overview
            </span>
          </Link>

          <Link
            to={`/custom-availability?flow=${selectedBookingFlow}`}
            className={cn(
              "flex items-center rounded-xl text-sm font-medium transition-all duration-200",
              "hover:bg-background-tertiary/50",
              location.pathname === "/custom-availability"
                ? "bg-primary-light text-primary"
                : "text-text-secondary hover:text-text-primary",
              isExpanded ? "px-3 py-2.5" : "justify-center p-2",
            )}
            title={!isExpanded ? "Custom Availability" : undefined}
          >
            <Clock className={cn("w-5 h-5 flex-shrink-0")} />
            <span
              className={cn(
                "transition-all duration-300 whitespace-nowrap",
                isExpanded
                  ? "opacity-100 ml-3"
                  : "opacity-0 ml-0 w-0 overflow-hidden",
              )}
            >
              Custom Availability
            </span>
          </Link>

          <Link
            to={`/booking-flows?flow=${selectedBookingFlow}&tab=settings`}
            className={cn(
              "flex items-center rounded-xl text-sm font-medium transition-all duration-200",
              "hover:bg-background-tertiary/50",
              location.pathname === "/booking-flows" &&
                location.search.includes("tab=settings")
                ? "bg-primary-light text-primary"
                : "text-text-secondary hover:text-text-primary",
              isExpanded ? "px-3 py-2.5" : "justify-center p-2",
            )}
            title={!isExpanded ? "Flow Config" : undefined}
          >
            <Sliders className={cn("w-5 h-5 flex-shrink-0")} />
            <span
              className={cn(
                "transition-all duration-300 whitespace-nowrap",
                isExpanded
                  ? "opacity-100 ml-3"
                  : "opacity-0 ml-0 w-0 overflow-hidden",
              )}
            >
              Flow Config
            </span>
          </Link>

          <Link
            to={`/booking-flows?flow=${selectedBookingFlow}&tab=questions`}
            className={cn(
              "flex items-center rounded-xl text-sm font-medium transition-all duration-200",
              "hover:bg-background-tertiary/50",
              location.pathname === "/booking-flows" &&
                location.search.includes("tab=questions")
                ? "bg-primary-light text-primary"
                : "text-text-secondary hover:text-text-primary",
              isExpanded ? "px-3 py-2.5" : "justify-center p-2",
            )}
            title={!isExpanded ? "Questions" : undefined}
          >
            <HelpCircle className={cn("w-5 h-5 flex-shrink-0")} />
            <span
              className={cn(
                "transition-all duration-300 whitespace-nowrap",
                isExpanded
                  ? "opacity-100 ml-3"
                  : "opacity-0 ml-0 w-0 overflow-hidden",
              )}
            >
              Questions
            </span>
          </Link>

          <Link
            to={`/analytics`}
            className={cn(
              "flex items-center rounded-xl text-sm font-medium transition-all duration-200",
              "hover:bg-background-tertiary/50",
              location.pathname === "/analytics"
                ? "bg-primary-light text-primary"
                : "text-text-secondary hover:text-text-primary",
              isExpanded ? "px-3 py-2.5" : "justify-center p-2",
            )}
            title={!isExpanded ? "Analytics" : undefined}
          >
            <BarChart3 className={cn("w-5 h-5 flex-shrink-0")} />
            <span
              className={cn(
                "transition-all duration-300 whitespace-nowrap",
                isExpanded
                  ? "opacity-100 ml-3"
                  : "opacity-0 ml-0 w-0 overflow-hidden",
              )}
            >
              Analytics
            </span>
          </Link>
        </nav>
      ) : (
        /* Spacer when no flow is selected */
        <div className="flex-1" />
      )}

      {/* Bottom Section - Organization, Dashboard, Settings, User */}
      <div
        className={cn(
          "border-t border-border-light/30 space-y-1 transition-all duration-300",
          isExpanded ? "p-4" : "p-2",
        )}
      >
        {/* Organization Switcher */}
        <div
          className={cn(
            "mb-2 transition-all duration-300",
            isExpanded ? "opacity-100" : "opacity-0 h-0 overflow-hidden",
          )}
        >
          <TenantSwitcher variant="sidebar" showDropdownArrow={true} />
        </div>

        {/* Set Availability Link (Tenant-level) */}
        <Link
          to="/availability"
          className={cn(
            "flex items-center rounded-xl text-sm font-medium transition-all duration-200",
            "hover:bg-background-tertiary/50",
            location.pathname === "/availability"
              ? "bg-primary-light text-primary"
              : "text-text-secondary hover:text-text-primary",
            isExpanded ? "px-3 py-2.5" : "justify-center p-2",
          )}
          title={!isExpanded ? "Set Availability" : undefined}
        >
          <CalendarClock className={cn("w-5 h-5 flex-shrink-0")} />
          <span
            className={cn(
              "transition-all duration-300 whitespace-nowrap",
              isExpanded
                ? "opacity-100 ml-3"
                : "opacity-0 ml-0 w-0 overflow-hidden",
            )}
          >
            Set Availability
          </span>
        </Link>

        {/* Dashboard Link */}
        <Link
          to="/dashboard"
          className={cn(
            "flex items-center rounded-xl text-sm font-medium transition-all duration-200",
            "hover:bg-background-tertiary/50",
            location.pathname === "/dashboard"
              ? "bg-primary-light text-primary"
              : "text-text-secondary hover:text-text-primary",
            isExpanded ? "px-3 py-2.5" : "justify-center p-2",
          )}
          title={!isExpanded ? "Dashboard" : undefined}
        >
          <LayoutDashboard className={cn("w-5 h-5 flex-shrink-0")} />
          <span
            className={cn(
              "transition-all duration-300 whitespace-nowrap",
              isExpanded
                ? "opacity-100 ml-3"
                : "opacity-0 ml-0 w-0 overflow-hidden",
            )}
          >
            Dashboard
          </span>
        </Link>

        {/* View Team Link (Tenant-level) */}
        <Link
          to="/team"
          className={cn(
            "flex items-center rounded-xl text-sm font-medium transition-all duration-200",
            "hover:bg-background-tertiary/50",
            location.pathname === "/team"
              ? "bg-primary-light text-primary"
              : "text-text-secondary hover:text-text-primary",
            isExpanded ? "px-3 py-2.5" : "justify-center p-2",
          )}
          title={!isExpanded ? "View Team" : undefined}
        >
          <Users className={cn("w-5 h-5 flex-shrink-0")} />
          <span
            className={cn(
              "transition-all duration-300 whitespace-nowrap",
              isExpanded
                ? "opacity-100 ml-3"
                : "opacity-0 ml-0 w-0 overflow-hidden",
            )}
          >
            View Team
          </span>
        </Link>

        {/* Settings Link */}
        <Link
          to="/settings"
          className={cn(
            "flex items-center rounded-xl text-sm font-medium transition-all duration-200",
            "hover:bg-background-tertiary/50",
            location.pathname === "/settings"
              ? "bg-primary-light text-primary"
              : "text-text-secondary hover:text-text-primary",
            isExpanded ? "px-3 py-2.5" : "justify-center p-2",
          )}
          title={!isExpanded ? "Settings" : undefined}
        >
          <Settings className={cn("w-5 h-5 flex-shrink-0")} />
          <span
            className={cn(
              "transition-all duration-300 whitespace-nowrap",
              isExpanded
                ? "opacity-100 ml-3"
                : "opacity-0 ml-0 w-0 overflow-hidden",
            )}
          >
            Settings
          </span>
        </Link>

        {/* User Profile */}
        <DropdownMenu
          position="top"
          trigger={
            <button
              className={cn(
                "flex items-center rounded-xl text-sm font-medium transition-all duration-200",
                "hover:bg-background-tertiary/50",
                "text-text-secondary hover:text-text-primary",
                isExpanded ? "px-3 py-2.5" : "justify-center p-2",
                "w-full mt-1",
              )}
            >
              <Avatar
                src={user?.imageUrl}
                alt={user?.fullName || "User"}
                fallback={
                  user?.firstName?.[0] ||
                  user?.emailAddresses[0]?.emailAddress[0] ||
                  "U"
                }
                size="sm"
                className="flex-shrink-0"
              />
              <span
                className={cn(
                  "transition-all duration-300 whitespace-nowrap",
                  isExpanded
                    ? "opacity-100 ml-3"
                    : "opacity-0 ml-0 w-0 overflow-hidden",
                )}
              >
                {user?.firstName ||
                  user?.emailAddresses[0]?.emailAddress.split("@")[0]}
              </span>
            </button>
          }
        >
          <DropdownMenuLabel>
            <div className="flex items-center space-x-3">
              <Avatar
                src={user?.imageUrl}
                alt={user?.fullName || "User"}
                fallback={user?.firstName?.[0] || "U"}
                size="md"
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-text-primary truncate">
                  {user?.fullName || user?.firstName || "User"}
                </p>
                <p className="text-xs text-text-secondary truncate">
                  {user?.emailAddresses[0]?.emailAddress}
                </p>
              </div>
            </div>
          </DropdownMenuLabel>

          <DropdownMenuItem onClick={handleSignOut} destructive>
            <LogOut className="mr-3 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenu>
      </div>
    </aside>
  );
}
